import { api, type PostingIdentity } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { ListingProfileCard } from "./ListingProfileCard";
import { accountVerificationFrom } from "../lib/accountVerification";

type RfqRequesterSummary = {
  name: string;
  profileImageUrl: string | null;
  memberSince: string | null;
  city: string | null;
  province: string | null;
  rfqsPosted: number;
  rfqsAwarded: number;
  rating: number | null;
  reviewCount: number;
};

const isNullableString = (value: unknown): value is string | null => value === null || typeof value === "string";
const isCount = (value: unknown): value is number => typeof value === "number" && Number.isSafeInteger(value) && value >= 0;

// useListing's legacy transport is untyped; older servers may omit the new public summary.
export function isRequesterSummary(value: unknown): value is RfqRequesterSummary {
  return typeof value === "object" && value !== null
    && "name" in value && typeof value.name === "string"
    && "profileImageUrl" in value && isNullableString(value.profileImageUrl)
    && "memberSince" in value && isNullableString(value.memberSince)
    && "city" in value && isNullableString(value.city)
    && "province" in value && isNullableString(value.province)
    && "rfqsPosted" in value && isCount(value.rfqsPosted)
    && "rfqsAwarded" in value && isCount(value.rfqsAwarded)
    && "rating" in value && (value.rating === null || (typeof value.rating === "number" && Number.isFinite(value.rating) && value.rating >= 1 && value.rating <= 5))
    && "reviewCount" in value && isCount(value.reviewCount);
}

export function RfqRequesterCard({ summary, ownerUserId, postingIdentity }: { postingIdentity?: PostingIdentity; summary: unknown; ownerUserId?: number | null }) {
  const { t } = useI18n();
  const requester = isRequesterSummary(summary) ? summary : null;
  return <ListingProfileCard postingIdentity={postingIdentity} title={t("detail.rfq.aboutRequester")}
    profile={{ name: requester?.name || t("detail.rfq.requester"), role: t("detail.item.member"),
      location: requester ? [requester.city, requester.province].filter(Boolean).join(", ") : null,
      memberSince: requester?.memberSince, imageUrl: api.apiAssetUrl(requester?.profileImageUrl),
      href: requester && ownerUserId && Number.isSafeInteger(ownerUserId) && ownerUserId > 0 ? `/u/${ownerUserId}` : null,
      ...accountVerificationFrom(requester) }}
    statistics={[{ label: t("detail.rfq.rfqsPosted"), value: requester?.rfqsPosted }, { label: t("detail.rfq.rfqsAwarded"), value: requester?.rfqsAwarded }]}
    rating={requester ? { average: requester.rating, count: requester.reviewCount } : null} />;
}
